import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../../../context/SidebarContext';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';
import { useDispatch, useSelector } from 'react-redux';
import { createWorker } from '../../../modules/worker/actions';
import { IWorkerCreate } from '../../../interfaces/worker.interface';
import { AppDispatch } from '../../../store';
import { WorkerCreateFormInputs } from '../../../interfaces'
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import {
  selectWorkerCreateError,
  selectWorkerCreateMessage,
  selectWorkerCreateLoading,
  IWorker,
  fetchPendingWorkerrList,
  selectPendingWorkerList,
  selectPendingWorkerListCount,
  resentWokreInvitation,
  selectSendInviteMessage,
  selectSendInviteError
} from '../../../modules/worker';
import { fetchRoleList, IRole } from '../../../modules/role';
import { FetchResponse, OptionType } from '../../../interfaces/paginatedSearchSelect.interface';
import PaginatedMultiSelect from '../../../components/PaginatedMultiSelect'; // Re-importing

import TabList from '@mui/lab/TabList';
import TabContext from '@mui/lab/TabContext';
import Grid from '@mui/material/Grid';
import { Box, FormGroup, Stack, TableRow, Typography, InputAdornment } from '@mui/material';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SearchIcon from '@mui/icons-material/Search';


import {
  PageContainer,
  CustomTab,
  PageTitle,
  PageHeader,
  AddWorkerButton,
  Label,
  CustomTextField,
  CustomTabPanel,
  InvitationContainer,
  StyledCheckbox,
  ButtonGroupContainer,
  CancelButton,
  CreateProfileButton,
  StyledTableContainer,
  StyledTable,
  StyledTableBody,
  StyledTableCell,
  PaginationFooter,
  InvitationStatusTag,
  InvitationAction,
  SearchContainer,
  SearchInput
} from './styles';
import CustomTableHeader from '../../../components/CustomHeader';
import CustomPagination from '../../../components/CustomPagination';
import AlertPopup from '../../../components/common/AlertPopup';
import dayjs from 'dayjs';


const schema = yup.object().shape({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup.string().required('Email is required').matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Invalid email format'),
  phoneNumber: yup.string().required('Phone number is required').matches(/^\d+$/, 'Only digits are allowed'),
  emergencyContact: yup.string().required('Emergency contact is required').matches(/^\d+$/, 'Only digits are allowed'),
  preferredRole: yup.array().of(yup.object().shape({
    role_id: yup.number().required(),
    role_name: yup.string().required()
  })).min(1, 'Preferred role is required'),
});

const WorkerCreatePage: React.FC = () => {
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();
  const navigate = useNavigate();
  useEffect(() => {
    setPath([
      { title: 'Management', path: '/' },
      { title: 'Worker', path: '/workers' },
      { title: 'Add New Worker', path: '' },
    ]);
    setActivePage('worker');
    setIsShow(true);
  }, [setPath, setActivePage, setIsShow]);

  const dispatch: AppDispatch = useDispatch();
  const loading = useSelector(selectWorkerCreateLoading);
  const error = useSelector(selectWorkerCreateError);
  const message = useSelector(selectWorkerCreateMessage);
  const workerList = useSelector(selectPendingWorkerList);
  const totalWorkers = useSelector(selectPendingWorkerListCount);
  const sendInviteMessage = useSelector(selectSendInviteMessage);
  const sendInviteError = useSelector(selectSendInviteError);


  const { handleSubmit, control, reset, formState: { errors } } = useForm<WorkerCreateFormInputs>({
    resolver: yupResolver(schema) as any,
  });

  const [value, setValue] = useState('create_new_worker');
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof IWorker | string>('user.first_name');
  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);

  const fetchRoleOptions = React.useCallback(async (search: string, page: number): Promise<FetchResponse<OptionType>> => {
    const action = await dispatch(fetchRoleList({
      page,
      pageSize: 10,
      search,
    }) as any);

    if (action.payload) {
      const payload = action.payload;
      // Handle various response structures: results inside data, direct results, or array directly
      const roles = Array.isArray(payload)
        ? payload
        : (payload.results || (payload.data && Array.isArray(payload.data) ? payload.data : (payload.data?.results || [])));

      const mappedRoles = Array.isArray(roles) ? roles.map((r: IRole) => ({
        id: r.role_id,
        name: r.role_name,
      })) : [];

      const count = payload.count || payload.data?.count || (Array.isArray(payload) ? payload.length : 0);

      return {
        options: mappedRoles,
        hasMore: count > page * 10,
      };
    }

    return {
      options: [],
      hasMore: false,
    };
  }, [dispatch]);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  const onSubmit = (data: WorkerCreateFormInputs) => {
    const workerData: IWorkerCreate = {
      first_name: data.firstName,
      last_name: data.lastName,
      email: data.email,
      phone_number: data.phoneNumber,
      emergency_contact: data.emergencyContact,
      preferred_roles: data.preferredRole?.map(r => r.role_id),
      send_invitation: sendInvite
    };
    dispatch(createWorker(workerData) as any)
      .then(() => {
        dispatch(fetchPendingWorkerrList({
          page,
          pageSize: rowsPerPage,
        }))
      });
  };

  useEffect(() => {
    if (message) {
      setAlert({ message: 'Worker Created!', severity: 'success' });
      reset();
    }
    if (error) {
      setAlert({ message: `${error}!`, severity: 'error' });
      reset()
    }
  }, [message, error, reset]);

  /**
   * Worker Listing
   */

  const rowsPerPage = 10;
  type Order = 'asc' | 'desc';

  interface Column {
    id: keyof IWorker | string;
    label: string;
    numeric?: boolean;
    disableSorting?: boolean;
  }

  const [page, setPage] = useState(1);
  const [sendInvite, setSendInvite] = useState<boolean>(true)
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');

  function descendingComparator<T>(a: T, b: T, orderBy: keyof T): number {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
  }

  const getComparator = React.useCallback(<Key extends keyof any>(
    order: Order,
    orderBy: Key,
  ): (a: { [key in Key]: any }, b: { [key in Key]: any }) => number => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset to page 1 on new search
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  function stableSort<T>(array: T[], comparator: (a: T, b: T) => number): T[] {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  const COLUMN_MAPPING: { [key: string]: keyof IWorker | string } = {
    'ID No': 'worker_id',
    'First Name': 'user.first_name',
    'Last Name': 'user.last_name',
    'Email ID': 'user.email',
    'Phone Number': 'user.phone_number',
    'Status': 'invitation_status',
    'Last Invite Sent': 'invitation_sent_on',
    'Action': '',
  };

  const WORKER_COLUMNS: Column[] = Object.entries(COLUMN_MAPPING).map(([label, id]) => ({
    id: id,
    label: label,
    numeric: id === 'worker_id',
    disableSorting: !(id === 'user.first_name' || id === 'worker_id'),
  }));

  useEffect(() => {
    dispatch(fetchPendingWorkerrList({
      page,
      pageSize: rowsPerPage,
      search: debouncedSearch
    }))
  }, [dispatch, page, rowsPerPage, debouncedSearch])

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string,
  ) => {
    const propertyKey = property as keyof IWorker | string;

    const columnDef = WORKER_COLUMNS.find(col => col.id === property);
    if (columnDef?.disableSorting) {
      return;
    }

    const isAsc = orderBy === propertyKey && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(propertyKey);
  };

  const sortedData = useMemo(() => {
    if (!workerList) return [];
    // Client-side sorting only for the current page's data
    return stableSort(workerList, getComparator(order, orderBy as keyof IWorker));
  }, [order, orderBy, workerList, getComparator]);

  const totalPages = Math.ceil(totalWorkers / rowsPerPage);

  const handleSendInvite = (workerId: number) => {
    dispatch(resentWokreInvitation(workerId) as any)
  }

  useEffect(() => {
    if (sendInviteMessage) {
      setAlert({ message: 'Invitation Sent!', severity: 'success' });
      reset();
    }
    if (sendInviteError) {
      setAlert({ message: 'Invitation not send', severity: 'error' })
      reset();
    }
  }, [sendInviteMessage, sendInviteError, reset]);

  return (
    <PageContainer>
      {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
      <TabContext value={value}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList onChange={handleChange} aria-label="lab API tabs example">
            <CustomTab label="Add New Worker" value="create_new_worker" />
            <CustomTab label="Invite Worker" value="invite_worker" />
          </TabList>
        </Box>
        <CustomTabPanel value="create_new_worker">
          <PageHeader>
            <PageTitle>Create New Worker Profile</PageTitle>
            <AddWorkerButton onClick={() => navigate('/worker/bulk-upload')} startIcon={<AddCircleOutlineIcon />}>
              Bulk Upload
            </AddWorkerButton>
          </PageHeader>
          <Box>
            <Grid container spacing={2}>
              <Grid size={6}>
                <FormGroup>
                  <Label>First name</Label>
                  <Controller
                    name="firstName"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <CustomTextField
                        {...field}
                        placeholder="Enter worker's first name"
                        error={!!errors.firstName}
                        helperText={errors.firstName?.message}
                      />
                    )}
                  />
                </FormGroup>
              </Grid>
              <Grid size={6}>
                <FormGroup>
                  <Label>Last name</Label>
                  <Controller
                    name="lastName"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <CustomTextField
                        {...field}
                        placeholder="Enter worker's last name"
                        error={!!errors.lastName}
                        helperText={errors.lastName?.message}
                      />
                    )}
                  />
                </FormGroup>
              </Grid>
              <Grid size={6}>
                <FormGroup>
                  <Label>Email Address</Label>
                  <Controller
                    name="email"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <CustomTextField
                        {...field}
                        placeholder="Enter worker's email"
                        error={!!errors.email}
                        helperText={errors.email?.message}
                      />
                    )}
                  />
                </FormGroup>
              </Grid>
              <Grid size={6}>
                <FormGroup>
                  <Label>Phone Number</Label>
                  <Controller
                    name="phoneNumber"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <CustomTextField
                        {...field}
                        placeholder="Enter worker's phone number"
                        error={!!errors.phoneNumber}
                        helperText={errors.phoneNumber?.message}
                        type="tel"
                      />
                    )}
                  />
                </FormGroup>
              </Grid>
              <Grid size={6}>
                <FormGroup>
                  <Label>Emergency Contact</Label>
                  <Controller
                    name="emergencyContact"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <CustomTextField
                        {...field}
                        placeholder="Enter worker's emergency contact"
                        error={!!errors.emergencyContact}
                        helperText={errors.emergencyContact?.message}
                        type="tel"
                      />
                    )}
                  />
                </FormGroup>
              </Grid>
              <Grid size={6}>
                <FormGroup>
                  <Label>Preferred Role</Label>
                  <Controller
                    name="preferredRole"
                    control={control}
                    defaultValue={[]}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <PaginatedMultiSelect
                        fetchOptions={fetchRoleOptions}
                        getOptionLabel="name"
                        value={value ? value.map((r: any) => ({ id: r.role_id, name: r.role_name })) : []}
                        onChange={(newValue: any[]) => onChange(newValue.map(v => ({ role_id: v.id, role_name: v.name })))}
                        placeholder="Search and select role"
                        fullWidth
                        error={!!error}
                        size='medium'
                        helperText={error?.message}
                        multiple
                      />
                    )}
                  />
                </FormGroup>
              </Grid>
            </Grid>
          </Box>
          <InvitationContainer>
            <Grid container>
              <Grid size={6}>
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Stack direction="row" alignItems="center" spacing={0} flexShrink={1}>
                    <StyledCheckbox
                      onChange={(event) => { setSendInvite(event.target.checked) }}
                      checked={sendInvite}
                    />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Send Onboarding Invitation
                    </Typography>
                  </Stack>
                </Stack>
              </Grid>
              <Grid size={6}>
                <Box flexShrink={0} textAlign="right">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: 'italic', whiteSpace: 'nowrap' }}
                  >
                    (Requires valid email address)
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <Box sx={{ mt: 0.5, pl: '32px' }}>
              <Typography variant="body2" color="text.primary">
                Send an invitation link for the worker to complete their onboarding details.
              </Typography>
            </Box>
          </InvitationContainer>
          <ButtonGroupContainer>
            <CancelButton variant="outlined" onClick={() => navigate('/workers')}>
              Cancel
            </CancelButton>
            <CreateProfileButton variant="contained" onClick={handleSubmit(onSubmit)} disabled={loading}>
              {loading ? 'Creating...' : 'Create Worker Profile'}
            </CreateProfileButton>
          </ButtonGroupContainer>
        </CustomTabPanel>
        <CustomTabPanel value="invite_worker">
          <PageHeader>
            <PageTitle>Invite Worker For Onboarding</PageTitle>
            <SearchContainer>
              <SearchInput
                placeholder="Search workers..."
                variant="standard"
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  disableUnderline: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'grey.500', fontSize: '1.2rem' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </SearchContainer>
          </PageHeader>

          <StyledTableContainer>
            <StyledTable stickyHeader>
              <CustomTableHeader
                columns={WORKER_COLUMNS}
                order={order}
                orderBy={orderBy as string}
                onRequestSort={handleRequestSort}
              />

              <StyledTableBody>
                {sortedData.map((worker) => (
                  <TableRow key={worker.worker_id} hover>
                    <StyledTableCell>{worker.worker_id}</StyledTableCell>
                    <StyledTableCell>{worker.user.first_name}</StyledTableCell>
                    <StyledTableCell>{worker.user.last_name}</StyledTableCell>
                    <StyledTableCell>{worker.user.email}</StyledTableCell>
                    <StyledTableCell>{worker.emergency_contact}</StyledTableCell>
                    <StyledTableCell>
                      <InvitationStatusTag status={worker.invitation_status}>
                        {worker.invitation_status}
                      </InvitationStatusTag>
                    </StyledTableCell>
                    <StyledTableCell>{(worker.invitation_sent_on) ? dayjs(worker.invitation_sent_on).format('DD-MM-YYYY HH:mm:ss') : 'N/A'}</StyledTableCell>
                    <StyledTableCell>
                      <InvitationAction
                        onClick={() => handleSendInvite(worker.worker_id)}
                        status={worker.invitation_status}
                      >
                        {(worker.invitation_status === 'invitation_sent') ? "Resend Invite" : "Invite"}
                      </InvitationAction>
                    </StyledTableCell>
                  </TableRow>
                ))}
              </StyledTableBody>
            </StyledTable>
          </StyledTableContainer>
          <PaginationFooter>
            <Box>
              Showing {workerList?.length || 0} of {totalWorkers} results
            </Box>
            <CustomPagination
              totalPages={totalPages}
              currentPage={page}
              onPageChange={handlePageChange}
            />
          </PaginationFooter>
        </CustomTabPanel>
      </TabContext>
    </PageContainer>
  );
};

export default WorkerCreatePage;